// FILE: CollegeStudentDirectory.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  Search,
  UserCheck,
  UserX,
  Users,
  Edit2,
  Archive,
  RotateCcw,
  Key,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  X,
  ShieldOff,
  ShieldCheck,
  ShieldAlert,
  CheckSquare,
  FolderOpen,
  Trash2
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

// --- Inline bulk-confirm modal ------------------------------------------------
const BulkConfirmModal = ({ action, count, loading, onConfirm, onCancel }) => {
  const cfg = {
    ARCHIVE:   {
      label: 'Archive',
      color: 'red',
      icon: <Archive size={20} />,
      desc: `Archive ${count} student(s). All records are preserved and credits are refunded to your account. You can restore them later.`,
    },
    RESTORE:   {
      label: 'Restore',
      color: 'green',
      icon: <RotateCcw size={20} />,
      desc: `Restore ${count} archived student(s). Each restore will consume 1 student credit from your account.`,
    },
    ACTIVE:    {
      label: 'Set Active',
      color: 'green',
      icon: <ShieldCheck size={20} />,
      desc: `Mark ${count} student(s) as Active. They will be able to log in.`,
    },
    INACTIVE:  {
      label: 'Set Inactive',
      color: 'gray',
      icon: <ShieldOff size={20} />,
      desc: `Mark ${count} student(s) as Inactive. Their access will be disabled.`,
    },
    SUSPENDED: {
      label: 'Suspend',
      color: 'orange',
      icon: <ShieldAlert size={20} />,
      desc: `Suspend ${count} student(s). Their access will be immediately revoked.`,
    },
  }[action];

  const colorMap = {
    red:    { btn: '#DC2626', btnHover: '#B91C1C', badgeBg: '#FEF2F2', badgeColor: '#DC2626', badgeBorder: '#FECACA' },
    green:  { btn: '#16A34A', btnHover: '#15803D', badgeBg: '#DCFCE7', badgeColor: '#16A34A', badgeBorder: '#BBF7D0' },
    gray:   { btn: '#475569', btnHover: '#334155', badgeBg: '#F1F5F9', badgeColor: '#64748B', badgeBorder: '#E2E8F0' },
    orange: { btn: '#EA580C', btnHover: '#C2410C', badgeBg: '#FFF7ED', badgeColor: '#EA580C', badgeBorder: '#FED7AA' },
  }[cfg.color];

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15,23,42,0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: 16,
    }}>
      <div style={{
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        border: '1px solid var(--border-subtle, #E2E8F0)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 40, height: 40, borderRadius: '10px',
              background: colorMap.badgeBg,
              border: `1px solid ${colorMap.badgeBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colorMap.badgeColor, flexShrink: 0,
            }}>
              {cfg.icon}
            </span>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', margin: 0 }}>
              Confirm: {cfg.label}
            </h3>
          </div>
          <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: 16 }}>
          {cfg.desc}
        </p>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderRadius: '10px',
          background: colorMap.badgeBg, border: `1px solid ${colorMap.badgeBorder}`,
          color: colorMap.badgeColor, fontSize: '0.875rem', fontWeight: 600, marginBottom: 20,
        }}>
          <CheckSquare size={16} />
          {count} student{count !== 1 ? 's' : ''} selected
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} disabled={loading} style={{
            flex: 1, padding: '10px 16px',
            border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: '10px',
            background: '#FFFFFF', cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: 600, color: '#374151',
          }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{
            flex: 1, padding: '10px 16px',
            border: 'none', borderRadius: '10px',
            background: colorMap.btn, color: '#FFFFFF', cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: loading ? 0.6 : 1,
          }}>
            {loading ? (
              <><div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />Processing...</>
            ) : (
              <>{cfg.icon} Confirm {cfg.label}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main component -----------------------------------------------------------
const CollegeStudentDirectory = ({
  collegeId,
  onEditStudent,
  onDeleteStudent,
  onRestoreStudent,
  onResetPassword,
  onBulkActionSuccess,
}) => {
  const [students, setStudents]                 = useState([]);
  const [allStudentsForFilter, setAllStudentsForFilter] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [globalFilter, setGlobalFilter]         = useState('');
  const [statusFilter, setStatusFilter]         = useState([]);
  const [branchFilter, setBranchFilter]         = useState([]);
  const [semesterFilter, setSemesterFilter]     = useState([]);
  const [pagination, setPagination]             = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting]                   = useState([]);
  const [openDropdown, setOpenDropdown]         = useState(null);

  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [bulkConfirm, setBulkConfirm]   = useState(null);
  const [bulkLoading, setBulkLoading]   = useState(false);
  const [bulkResult, setBulkResult]     = useState(null);

  // Whether we are currently viewing the archived/deleted students
  const isDeletedView = statusFilter.includes('DELETED');

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.filter-dropdown-container')) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch filter options (branches, semesters) from active students only
  useEffect(() => {
    if (!collegeId) return;
    axios.post(`${API_BASE_URL}/students/list`, { college_id: collegeId, limit: 1000 })
      .then(res => { if (res.data.students) setAllStudentsForFilter(res.data.students); })
      .catch(err => console.error('Failed to fetch filter options:', err));
  }, [collegeId]);

  const uniqueValues = useMemo(() => {
    const combined = [...new Map([...allStudentsForFilter, ...students].map(s => [s.student_id, s])).values()];
    return {
      branches:  [...new Set(combined.map(s => s.branch).filter(Boolean))].sort(),
      semesters: [...new Set(combined.map(s => s.semester).filter(Boolean))].sort((a, b) => a - b),
      // DELETED is always available; order matters for UX
      statuses: ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'DELETED'],
    };
  }, [students, allStudentsForFilter]);

  // Fetch students with soft-delete awareness
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/students/list`, {
        college_id:      collegeId,
        status:          statusFilter.length > 0 ? statusFilter : null,
        branch:          branchFilter.length  > 0 ? branchFilter  : null,
        semester:        semesterFilter.length > 0 ? semesterFilter : null,
        // include_deleted is auto-set on the backend when status includes 'DELETED';
        // also send it explicitly from the client for clarity.
        include_deleted: isDeletedView,
      });
      setStudents(res.data.students || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
      setError('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [collegeId, statusFilter, branchFilter, semesterFilter, isDeletedView]);

  useEffect(() => {
    if (collegeId) fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, branchFilter, semesterFilter]);

  // -- Selection helpers -------------------------------------------------------
  const toggleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllPage = useCallback((pageIds, allSelected) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach(id => next.delete(id));
      else             pageIds.forEach(id => next.add(id));
      return next;
    });
  }, []);

  // -- Bulk action -------------------------------------------------------------
  const handleBulkAction = async () => {
    if (!bulkConfirm) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Map frontend action names to backend action names
      const apiAction = bulkConfirm === 'ARCHIVE' ? 'DELETE' : bulkConfirm;

      const res = await axios.post(`${API_BASE_URL}/students/bulk-action`, {
        college_id:  collegeId,
        student_ids: Array.from(selectedIds),
        action:      apiAction,
        action_by:   user.username || user.id || 'admin',
      });

      const { processed, skipped } = res.data;
      const actionLabel = bulkConfirm === 'ARCHIVE'  ? 'archived'
                        : bulkConfirm === 'RESTORE'  ? 'restored'
                        : 'updated';
      setBulkResult({
        type:    'success',
        message: `${processed} student(s) ${actionLabel} successfully.`
          + (skipped > 0 ? ` (${skipped} skipped)` : ''),
      });
      setBulkConfirm(null);
      setSelectedIds(new Set());
      await fetchStudents();
      // Refresh parent credits after any credit-changing operation
      if ((bulkConfirm === 'ARCHIVE' || bulkConfirm === 'RESTORE') && onBulkActionSuccess) {
        onBulkActionSuccess();
      }
    } catch (err) {
      console.error('Bulk action error:', err);
      setBulkResult({
        type:    'error',
        message: err.response?.data?.error || err.response?.data?.message || 'Bulk action failed. Please try again.',
      });
      setBulkConfirm(null);
    } finally {
      setBulkLoading(false);
    }
  };

  // -- Filter toggle -----------------------------------------------------------
  const toggleFilter = (value, current, setter) => {
    setter(current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
  };

  // -- Status badge renderer ---------------------------------------------------
  const StatusBadge = ({ status }) => {
    const styleMap = {
      ACTIVE:    { background: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0' },
      INACTIVE:  { background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' },
      SUSPENDED: { background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' },
      DELETED:   { background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' },
    };
    const st = styleMap[status] || styleMap.INACTIVE;
    const label = status === 'DELETED' ? 'ARCHIVED' : status;
    return (
      <span style={{
        ...st, borderRadius: '999px',
        fontSize: '11px', fontWeight: 700,
        padding: '2px 8px', display: 'inline-block',
      }}>
        {label}
      </span>
    );
  };

  // -- Table columns -----------------------------------------------------------
  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => {
        const pageIds = table.getRowModel().rows.map(r => r.original.student_id);
        const allSel  = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
        const someSel = pageIds.some(id => selectedIds.has(id));
        return (
          <IndeterminateCheckbox
            checked={allSel}
            indeterminate={someSel && !allSel}
            onChange={() => toggleSelectAllPage(pageIds, allSel)}
          />
        );
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.original.student_id)}
          onChange={() => toggleSelectOne(row.original.student_id)}
          onClick={e => e.stopPropagation()}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer focus:ring-blue-500"
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'student_id',
      header: 'Student ID',
      enableColumnFilter: false,
      cell: ({ getValue }) => (
        <span className="text-xs font-mono text-gray-500">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row, getValue }) => (
        <span className="font-medium text-gray-800" style={{ opacity: row.original.is_deleted ? 0.6 : 1 }}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-600">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'roll_number',
      header: 'Roll No',
      cell: ({ getValue }) => (
        <span className="text-sm font-medium text-blue-600">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'branch',
      header: ({ column }) => (
        <button style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280' }}
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
          Branch <ArrowUpDown size={12} />
        </button>
      ),
      cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue()}</span>,
    },
    {
      accessorKey: 'semester',
      header: ({ column }) => (
        <button style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280' }}
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
          Semester <ArrowUpDown size={12} />
        </button>
      ),
      cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue()}</span>,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <button style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280' }}
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
          Status <ArrowUpDown size={12} />
        </button>
      ),
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      accessorKey: 'last_login',
      header: 'Last Login',
      cell: ({ getValue }) => {
        const v = getValue();
        return v
          ? <span className="text-xs text-gray-600">{new Date(v).toLocaleDateString()}</span>
          : <span className="text-xs text-gray-400">Never</span>;
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Added On',
      cell: ({ getValue }) => (
        <span className="text-xs text-gray-600">{new Date(getValue()).toLocaleDateString()}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const student = row.original;
        if (student.is_deleted) {
          // Archived student — show only Restore action
          return (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                onClick={e => { e.stopPropagation(); onRestoreStudent && onRestoreStudent(student); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: '6px', border: '1px solid #BBF7D0', background: '#DCFCE7', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, color: '#16A34A', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#BBF7D0'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#DCFCE7'; }}
                title="Restore Student"
              >
                <RotateCcw size={13} /> Restore
              </button>
            </div>
          );
        }
        // Active student — show Edit, Reset Password, Archive
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={e => { e.stopPropagation(); onEditStudent && onEditStudent(student); }}
              style={{ padding: '6px', borderRadius: '6px', border: 'none', background: 'transparent', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              title="Edit Student"
            >
              <Edit2 size={15} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onResetPassword && onResetPassword(student); }}
              style={{ padding: '6px', borderRadius: '6px', border: 'none', background: 'transparent', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#FFFBEB'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              title="Reset Password"
            >
              <Key size={15} style={{ color: '#D97706' }} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDeleteStudent && onDeleteStudent(student); }}
              style={{ padding: '6px', borderRadius: '6px', border: 'none', background: 'transparent', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              title="Archive Student"
            >
              <Archive size={15} style={{ color: '#DC2626' }} />
            </button>
          </div>
        );
      },
    },
  ], [selectedIds, toggleSelectOne, toggleSelectAllPage, onEditStudent, onDeleteStudent, onRestoreStudent, onResetPassword]);

  const table = useReactTable({
    data: students,
    columns,
    state:                { globalFilter, pagination, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange:   setPagination,
    onSortingChange:      setSorting,
    getCoreRowModel:      getCoreRowModel(),
    getFilteredRowModel:  getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel:    getSortedRowModel(),
  });

  // Stats — archived students are included in total count
  const stats = useMemo(() => {
    const active    = students.filter(s => !s.is_deleted && s.status === 'ACTIVE').length;
    const inactive  = students.filter(s => !s.is_deleted && s.status === 'INACTIVE').length;
    const suspended = students.filter(s => !s.is_deleted && s.status === 'SUSPENDED').length;
    const archived  = students.filter(s => s.is_deleted).length;
    return { total: active + inactive + suspended + archived, active, inactive, suspended, archived };
  }, [students]);

  // Bulk actions differ based on view mode
  const bulkActions = isDeletedView ? [
    { action: 'RESTORE', label: 'Restore Selected', icon: <RotateCcw size={14} />, bg: '#16A34A', hoverBg: '#15803D' },
  ] : [
    { action: 'ACTIVE',    label: 'Set Active',   icon: <ShieldCheck size={14} />, bg: '#16A34A', hoverBg: '#15803D' },
    { action: 'INACTIVE',  label: 'Set Inactive', icon: <ShieldOff size={14} />,   bg: '#475569', hoverBg: '#334155' },
    { action: 'SUSPENDED', label: 'Suspend',      icon: <ShieldAlert size={14} />, bg: '#EA580C', hoverBg: '#C2410C' },
    { action: 'ARCHIVE',   label: 'Archive',      icon: <Archive size={14} />,     bg: '#DC2626', hoverBg: '#B91C1C' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid #BFDBFE', borderTopColor: 'var(--primary-blue, #4F46E5)', animation: 'spin 0.7s linear infinite', margin: '0 auto' }} />
          <p style={{ color: '#64748B', fontSize: '0.875rem', marginTop: 16 }}>Loading students...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px 24px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <AlertCircle style={{ color: '#DC2626', flexShrink: 0 }} size={22} />
        <div>
          <p style={{ fontWeight: 700, color: '#991B1B', margin: '0 0 2px' }}>Error</p>
          <p style={{ fontSize: '0.875rem', color: '#B91C1C', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  const MultiSelectDropdown = ({ label, options, selected, onToggle, isOpen, onToggleDropdown, type, placeholder }) => (
    <div className="relative filter-dropdown-container" style={{ flex: 1 }}>
      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
        {label}
      </label>
      <div
        onClick={() => onToggleDropdown(isOpen ? null : type)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px',
          background: '#FFFFFF',
          border: isOpen ? '1px solid var(--primary-blue, #4F46E5)' : '1px solid var(--border-subtle, #E2E8F0)',
          borderRadius: '10px', cursor: 'pointer',
          boxShadow: isOpen ? '0 0 0 3px rgba(79, 70, 229,0.1)' : 'none',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', overflow: 'hidden' }}>
          {selected.length === 0 ? (
            <span style={{ fontSize: '0.875rem', color: '#94A3B8' }}>{placeholder}</span>
          ) : (
            selected.map(val => {
              const displayVal = type === 'semester' ? `Sem ${val}` : val === 'DELETED' ? 'Archived' : val;
              return (
                <span key={val} style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px',
                  background: val === 'DELETED' ? '#FFF7ED' : '#EFF6FF',
                  color: val === 'DELETED' ? '#C2410C' : 'var(--primary-blue, #4F46E5)',
                  borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
                  border: val === 'DELETED' ? '1px solid #FED7AA' : '1px solid #BFDBFE',
                }}>
                  {displayVal}
                  <X size={12} style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onToggle(val); }} />
                </span>
              );
            })
          )}
        </div>
        <ChevronDown size={16} style={{ color: '#94A3B8', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
      </div>
      {isOpen && (
        <div style={{
          position: 'absolute', zIndex: 50, top: 'calc(100% + 6px)', left: 0, width: '100%',
          background: '#FFFFFF', border: '1px solid var(--border-subtle, #E2E8F0)',
          borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          maxHeight: 240, overflowY: 'auto',
        }}>
          {options.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: '0.875rem', color: '#94A3B8', fontStyle: 'italic' }}>No options available</div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              {options.map(option => {
                const displayLabel = type === 'semester' ? `Sem ${option}` : option === 'DELETED' ? 'Archived' : option;
                const isArchiveOption = option === 'DELETED';
                return (
                  <div key={option} onClick={e => { e.stopPropagation(); onToggle(option); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 14px', cursor: 'pointer',
                      background: selected.includes(option) ? (isArchiveOption ? '#FFF7F7' : '#F8FBFF') : 'transparent',
                      transition: 'background 0.1s',
                      borderTop: isArchiveOption ? '1px solid var(--border-subtle, #E2E8F0)' : 'none',
                    }}
                    onMouseEnter={e => { if (!selected.includes(option)) e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { if (!selected.includes(option)) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <input type="checkbox" checked={selected.includes(option)} readOnly
                      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: isArchiveOption ? '#DC2626' : 'var(--primary-blue, #4F46E5)' }} />
                    <span style={{ fontSize: '0.875rem', fontWeight: selected.includes(option) ? 600 : 400, color: isArchiveOption ? '#DC2626' : (selected.includes(option) ? 'var(--secondary-blue, #1E3A8A)' : '#374151') }}>
                      {displayLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {selected.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-subtle, #E2E8F0)', padding: '8px 14px', background: '#F8FAFC', borderRadius: '0 0 12px 12px' }}>
              <button onClick={e => { e.stopPropagation();
                if (type === 'branch')   setBranchFilter([]);
                if (type === 'semester') setSemesterFilter([]);
                if (type === 'status')   setStatusFilter([]);
              }} style={{ fontSize: '0.75rem', color: 'var(--primary-blue, #4F46E5)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Clear Selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* -- Stats cards -- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
        {/* Total Students */}
        <div
          onClick={() => setStatusFilter([])}
          style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative', cursor: 'pointer', transition: 'border 0.15s' }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'var(--primary-blue, #4F46E5)', borderRadius: '14px 14px 0 0' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
            <div>
              <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Total Students</p>
              <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0F172A', margin: 0, lineHeight: 1 }}>{stats.total}</p>
            </div>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: 'rgba(79, 70, 229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Users size={20} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
            </div>
          </div>
        </div>
        {/* Active */}
        <div
          onClick={() => setStatusFilter(['ACTIVE'])}
          style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative', cursor: 'pointer', transition: 'border 0.15s' }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#16A34A', borderRadius: '14px 14px 0 0' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
            <div>
              <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Active</p>
              <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0F172A', margin: 0, lineHeight: 1 }}>{stats.active}</p>
            </div>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: 'rgba(22,163,74,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <UserCheck size={20} style={{ color: '#16A34A' }} />
            </div>
          </div>
        </div>
        {/* Inactive */}
        <div
          onClick={() => setStatusFilter(['INACTIVE'])}
          style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative', cursor: 'pointer', transition: 'border 0.15s' }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#64748B', borderRadius: '14px 14px 0 0' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
            <div>
              <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Inactive</p>
              <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0F172A', margin: 0, lineHeight: 1 }}>{stats.inactive}</p>
            </div>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: 'rgba(100,116,139,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <UserX size={20} style={{ color: '#64748B' }} />
            </div>
          </div>
        </div>
        {/* Suspended */}
        <div
          onClick={() => setStatusFilter(['SUSPENDED'])}
          style={{ background: '#FFFFFF', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-subtle, #E2E8F0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative', cursor: 'pointer', transition: 'border 0.15s' }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#DC2626', borderRadius: '14px 14px 0 0' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
            <div>
              <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Suspended</p>
              <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0F172A', margin: 0, lineHeight: 1 }}>{stats.suspended}</p>
            </div>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: 'rgba(220,38,38,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <AlertCircle size={20} style={{ color: '#DC2626' }} />
            </div>
          </div>
        </div>
        {/* Archived — always visible card */}
        <div
          onClick={() => { if (!isDeletedView) setStatusFilter(['DELETED']); }}
          style={{
            background: '#FFFFFF', borderRadius: '14px', padding: '20px',
            border: isDeletedView ? '2px solid #FED7AA' : '1px solid var(--border-subtle, #E2E8F0)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', position: 'relative',
            cursor: isDeletedView ? 'default' : 'pointer',
            transition: 'border 0.15s',
          }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#F97316', borderRadius: '14px 14px 0 0' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
            <div>
              <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>
                Archived{!isDeletedView && <span style={{ fontSize: '0.6rem', marginLeft: 4, color: '#F97316' }}>↗</span>}
              </p>
              <p style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0F172A', margin: 0, lineHeight: 1 }}>{stats.archived}</p>
            </div>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FolderOpen size={20} style={{ color: '#F97316' }} />
            </div>
          </div>
        </div>
      </div>

      {/* -- Archived view banner -- */}
      {isDeletedView && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderRadius: '12px',
          background: '#FFF7ED', border: '1px solid #FED7AA',
          fontSize: '0.875rem', color: '#92400E',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOpen size={16} style={{ color: '#F97316' }} />
            <strong>Archived Student View</strong> — These students are not active. You can restore them below.
          </div>
          <button
            onClick={() => setStatusFilter([])}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C2410C', fontWeight: 600, fontSize: '0.8rem', textDecoration: 'underline' }}
          >
            ← Back to Directory
          </button>
        </div>
      )}

      {/* -- Bulk result toast -- */}
      {bulkResult && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderRadius: '12px',
          border: bulkResult.type === 'success' ? '1px solid #BBF7D0' : '1px solid #FECACA',
          background: bulkResult.type === 'success' ? '#DCFCE7' : '#FEF2F2',
          fontSize: '0.875rem', fontWeight: 600,
          color: bulkResult.type === 'success' ? '#166534' : '#991B1B',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {bulkResult.type === 'success'
              ? <CheckCircle size={16} style={{ color: '#16A34A' }} />
              : <AlertCircle size={16} style={{ color: '#DC2626' }} />
            }
            {bulkResult.message}
          </div>
          <button onClick={() => setBulkResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 12, opacity: 0.7, color: 'inherit' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* -- Bulk action toolbar -- */}
      {selectedIds.size > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 16px',
          background: isDeletedView ? 'rgba(22,163,74,0.05)' : 'rgba(79, 70, 229,0.05)',
          border: isDeletedView ? '1px solid rgba(22,163,74,0.15)' : '1px solid rgba(79, 70, 229,0.15)',
          borderRadius: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckSquare size={18} style={{ color: isDeletedView ? '#16A34A' : 'var(--primary-blue, #4F46E5)' }} />
            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: isDeletedView ? '#166534' : 'var(--secondary-blue, #1E3A8A)' }}>
              {selectedIds.size} student{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{ fontSize: '0.75rem', color: isDeletedView ? '#16A34A' : 'var(--primary-blue, #4F46E5)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}
            >
              Clear
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {bulkActions.map(({ action, label, icon, bg }) => (
              <button
                key={action}
                onClick={() => { setBulkResult(null); setBulkConfirm(action); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: '8px', border: 'none',
                  background: bg, color: '#FFFFFF',
                  fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* -- Filters -- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 400 }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }} />
            <input
              type="text"
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder={isDeletedView ? 'Search archived students...' : 'Search students...'}
              style={{
                width: '100%', paddingLeft: 38, paddingRight: 14, paddingTop: 9, paddingBottom: 9,
                border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: '10px',
                fontSize: '0.875rem', color: '#0F172A', outline: 'none', boxSizing: 'border-box', background: '#FFFFFF',
              }}
              onFocus={e => { e.target.style.border = '1px solid var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; }}
              onBlur={e => { e.target.style.border = '1px solid var(--border-subtle, #E2E8F0)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <button onClick={fetchStudents}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', border: '1px solid var(--border-subtle, #E2E8F0)',
              borderRadius: '10px', background: '#FFFFFF', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 600, color: '#374151', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
            onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <MultiSelectDropdown label="Filter by Branch" options={uniqueValues.branches}
            selected={branchFilter} onToggle={v => toggleFilter(v, branchFilter, setBranchFilter)}
            isOpen={openDropdown === 'branch'} onToggleDropdown={setOpenDropdown}
            type="branch" placeholder="Select Branches" />
          <MultiSelectDropdown label="Filter by Semester" options={uniqueValues.semesters}
            selected={semesterFilter} onToggle={v => toggleFilter(v, semesterFilter, setSemesterFilter)}
            isOpen={openDropdown === 'semester'} onToggleDropdown={setOpenDropdown}
            type="semester" placeholder="Select Semesters" />
          <MultiSelectDropdown label="Filter by Status" options={uniqueValues.statuses}
            selected={statusFilter} onToggle={v => toggleFilter(v, statusFilter, setStatusFilter)}
            isOpen={openDropdown === 'status'} onToggleDropdown={setOpenDropdown}
            type="status" placeholder="All Statuses" />
        </div>
      </div>

      {/* -- Table -- */}
      <div style={{ background: '#FFFFFF', borderRadius: '14px', border: isDeletedView ? '1px solid #FED7AA' : '1px solid var(--border-subtle, #E2E8F0)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: isDeletedView ? '#FFF7ED' : '#F8FAFC' }}>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id}>
                  {hg.headers.map(header => (
                    <th key={header.id} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: '0.7rem', fontWeight: 700, color: '#6B7280',
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      borderBottom: '1px solid var(--border-subtle, #E2E8F0)',
                      whiteSpace: 'nowrap',
                    }}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => {
                  const isSelected = selectedIds.has(row.original.student_id);
                  const isArchived = row.original.is_deleted;
                  return (
                    <tr
                      key={row.id}
                      style={{
                        background: isSelected
                          ? (isArchived ? '#FFF7ED' : '#EFF6FF')
                          : (isArchived ? '#FFFBF5' : '#FFFFFF'),
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        opacity: isArchived ? 0.85 : 1,
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = isArchived ? '#FFF3E4' : '#F8FAFC'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isSelected ? (isArchived ? '#FFF7ED' : '#EFF6FF') : (isArchived ? '#FFFBF5' : '#FFFFFF'); }}
                      onClick={() => toggleSelectOne(row.original.student_id)}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} style={{ padding: '14px 16px' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} style={{ padding: '48px 16px', textAlign: 'center' }}>
                    {isDeletedView ? (
                      <>
                        <FolderOpen size={40} style={{ color: '#FED7AA', margin: '0 auto 12px', display: 'block' }} />
                        <p style={{ color: '#94A3B8', fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>No archived students found</p>
                      </>
                    ) : (
                      <>
                        <Users size={40} style={{ color: '#E2E8F0', margin: '0 auto 12px', display: 'block' }} />
                        <p style={{ color: '#94A3B8', fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>No students found</p>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* -- Pagination -- */}
        {table.getPageCount() > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#F8FAFC', borderTop: '1px solid var(--border-subtle, #E2E8F0)',
            flexWrap: 'wrap', gap: 10,
          }}>
            <div style={{ fontSize: '0.8rem', color: '#64748B' }}>
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}–{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                students.length
              )}{' '}
              of {students.length} {isDeletedView ? 'archived ' : ''}students
              {selectedIds.size > 0 && (
                <span style={{ marginLeft: 10, color: 'var(--primary-blue, #4F46E5)', fontWeight: 700 }}>({selectedIds.size} selected)</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {[
                { fn: () => table.setPageIndex(0), disabled: !table.getCanPreviousPage(), icon: <ChevronsLeft size={16} /> },
                { fn: () => table.previousPage(), disabled: !table.getCanPreviousPage(), icon: <ChevronLeft size={16} /> },
              ].map((btn, i) => (
                <button key={i} onClick={btn.fn} disabled={btn.disabled}
                  style={{ padding: '6px', borderRadius: '8px', border: '1px solid var(--border-subtle, #E2E8F0)', background: '#FFFFFF', cursor: btn.disabled ? 'not-allowed' : 'pointer', opacity: btn.disabled ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>
                  {btn.icon}
                </button>
              ))}
              <span style={{ fontSize: '0.8rem', color: '#64748B', padding: '0 6px' }}>
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              {[
                { fn: () => table.nextPage(), disabled: !table.getCanNextPage(), icon: <ChevronRight size={16} /> },
                { fn: () => table.setPageIndex(table.getPageCount() - 1), disabled: !table.getCanNextPage(), icon: <ChevronsRight size={16} /> },
              ].map((btn, i) => (
                <button key={i} onClick={btn.fn} disabled={btn.disabled}
                  style={{ padding: '6px', borderRadius: '8px', border: '1px solid var(--border-subtle, #E2E8F0)', background: '#FFFFFF', cursor: btn.disabled ? 'not-allowed' : 'pointer', opacity: btn.disabled ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>
                  {btn.icon}
                </button>
              ))}
              <select value={table.getState().pagination.pageSize}
                onChange={e => table.setPageSize(Number(e.target.value))}
                style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: '8px', fontSize: '0.8rem', color: '#374151', background: '#FFFFFF' }}>
                {[10, 20, 50, 100].map(ps => (
                  <option key={ps} value={ps}>Show {ps}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* -- Bulk confirm modal -- */}
      {bulkConfirm && (
        <BulkConfirmModal
          action={bulkConfirm}
          count={selectedIds.size}
          loading={bulkLoading}
          onConfirm={handleBulkAction}
          onCancel={() => setBulkConfirm(null)}
        />
      )}
    </div>
  );
};

// --- Indeterminate checkbox --------------------------------------------------
const IndeterminateCheckbox = ({ checked, indeterminate, onChange }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer focus:ring-blue-500"
    />
  );
};

export default CollegeStudentDirectory;
